import React from 'react';
import type { RescueUnassignedInventoryDeviceItemGql } from '../../lib/rescueQueries';
import UnassignedDeviceRow from './UnassignedDeviceRow';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import styles from './Devices.module.scss';

// Device takes everything the Action column leaves, so Action lines up with the Activated
// table above it — the two stack on the Devices tab.
export const STOCK_COLUMNS: TableColumn[] = [
  { label: 'Device', width: `${100 - ACTION_COLUMN_WIDTH}%` },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%` },
];

interface StockTableProps {
  /** Devices for the current page (already sliced by the caller's client pagination). */
  devices: RescueUnassignedInventoryDeviceItemGql[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
  onAssign: (moduleId: string) => void;
  searchQuery: string;
}

/** Delivered devices not yet attached to a pet (formerly the "Unassigned" group). */
export default function StockTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  hasError,
  onRetry,
  onAssign,
  searchQuery,
}: StockTableProps) {
  const colCount = STOCK_COLUMNS.length;
  const showSkeleton = loading && totalCount === 0 && !hasError;

  return (
    <CollapsibleSection
      title="Stock"
      count={totalCount}
      description="Devices delivered to your rescue and not yet attached to a pet."
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <TableHead columns={STOCK_COLUMNS} />
          <tbody>
            {hasError ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load inventory.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={5} columns={colCount} />
            ) : totalCount === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No stock devices matching "${searchQuery}"` : 'No devices in stock'}
                </td>
              </tr>
            ) : (
              devices.map((d) => <UnassignedDeviceRow key={d.moduleId} device={d} onAssign={onAssign} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
